import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, Select, message } from 'antd';
import { userApi } from '../../services/api';
import { UserItem } from '../../services/types';

interface Props {
    open: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    editData?: UserItem;
    currentUserId?: string;
}

const roleOptions = [
    { label: '管理员', value: 'admin' },
    { label: '运营', value: 'operator' },
    { label: '普通用户', value: 'user' },
];

const UserFormModal: React.FC<Props> = ({
    open,
    onCancel,
    onSuccess,
    editData,
    currentUserId,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const isEdit = !!editData;
    const isSelfEdit = isEdit && editData?.id === currentUserId;

    useEffect(() => {
        if (!open) {
            form.resetFields();
            return;
        }

        // 先重置再回填，避免切换编辑对象时残留上一次输入（尤其是密码）
        form.resetFields();

        if (editData) {
            form.setFieldsValue({
                username: editData.username,
                email: editData.email,
                role: editData.role,
            });
        }
    }, [open, editData, form]);

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (isEdit) {
                // 编辑模式：只发送修改的字段
                const updatePayload: Record<string, string> = {};
                if (values.email !== editData!.email) updatePayload.email = values.email;
                if (values.role !== editData!.role) updatePayload.role = values.role;
                if (!isSelfEdit && values.password) updatePayload.password = values.password;

                await userApi.update(editData!.id, updatePayload);
                message.success('更新成功');
            } else {
                await userApi.create(values);
                message.success('创建成功');
            }
            onSuccess();
        } catch (error: any) {
            if (error.response?.data?.detail) {
                message.error(error.response.data.detail);
            } else if (error.errorFields) {
                // form validation error
            } else {
                message.error('操作失败');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={isEdit ? '编辑用户' : '添加用户'}
            open={open}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={loading}
            destroyOnHidden
            okText="保存"
            cancelText="取消"
        >
            <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item
                    label="用户名"
                    name="username"
                    rules={[
                        { required: true, message: '请输入用户名' },
                        { min: 3, max: 50, message: '3-50 个字符' },
                    ]}
                >
                    <Input placeholder="请输入用户名" disabled={isEdit} />
                </Form.Item>

                <Form.Item
                    label="邮箱"
                    name="email"
                    rules={[
                        { required: true, message: '请输入邮箱' },
                        { type: 'email', message: '请输入有效邮箱' },
                    ]}
                >
                    <Input placeholder="请输入邮箱" />
                </Form.Item>

                <Form.Item
                    label="角色"
                    name="role"
                    rules={[{ required: true, message: '请选择角色' }]}
                    initialValue="user"
                >
                    <Select options={roleOptions} placeholder="请选择角色" />
                </Form.Item>

                {!isSelfEdit ? (
                    <Form.Item
                        label={isEdit ? '新密码（不修改请留空）' : '密码'}
                        name="password"
                        rules={isEdit ? [] : [
                            { required: true, message: '请输入密码' },
                            { min: 8, message: '至少 8 个字符' },
                        ]}
                    >
                        <Input.Password placeholder={isEdit ? '留空则不修改' : '请输入密码'} />
                    </Form.Item>
                ) : (
                    <div style={{ color: '#71717a', fontSize: 13 }}>
                        当前账号密码请在左下角个人入口修改
                    </div>
                )}
            </Form>
        </Modal>
    );
};

export default UserFormModal;
